import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { shareAPI } from '../services/api';
import { Code2, Download, AlertCircle } from 'lucide-react';
import '../Components/css/SharedFile.css';

const getFileIcon = (extension) => {
  const iconMap = {
    '.py': '🐍',
    '.js': '📜',
    '.java': '☕',
    '.cpp': '⚙️',
    '.c': '©️',
    '.txt': '📄',
  };
  return iconMap[extension] || '📄';
};

const SharedFile = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadSharedFile();
  }, [token]);

  const loadSharedFile = async () => {
    try {
      setLoading(true);
      const data = await shareAPI.getSharedFile(token);
      setFile(data);
    } catch (err) {
      setError('Failed to load shared file. The link may be invalid or expired.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    const blob = new Blob([file.content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  if (loading) {
    return (
      <div className="shared-file-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading shared file...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="shared-file-container">
        <div className="error-state">
          <AlertCircle size={48} />
          <h2>File Not Found</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/login')} className="back-btn">
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="shared-file-wrapper">
      <div className="shared-file-left">
        <div className="shared-header">
          <div className="file-info">
            <span className="file-icon-large">{getFileIcon(file.extension)}</span>
            <div>
              <h1>{file.name}</h1>
              <p className="shared-by">Shared from Codii</p>
            </div>
          </div>
          <div className="header-actions">
            <button onClick={handleDownload} className="download-btn">
              <Download size={18} />
              Download
            </button>
            <button onClick={() => navigate('/login')} className="login-btn">
              Open in Codii
            </button>
          </div>
        </div>

        <div className="shared-file-content">
          <div className="code-header">
            <Code2 size={18} />
            <span>Read-only view</span>
          </div>
          <pre className="code-block">
            <code>{file.content || '// Empty file'}</code>
          </pre>
        </div>
      </div>

      <div className="shared-file-right">
        <div className="promo-content">
          <div className="codii-logo-large">
            <img src="/codii_logo_trans.png" alt="Codii" />
          </div>
          <h2>Want to edit this code?</h2>
          <p>Sign up for Codii to unlock the full coding experience</p>
          <ul className="feature-list">
            <li>✓ Online code editor for multiple languages</li>
            <li>✓ Real-time collaboration</li>
            <li>✓ File management system</li>
            <li>✓ Share your code easily</li>
          </ul>
          <button onClick={() => navigate('/register')} className="cta-btn">
            Get Started Free
          </button>
        </div>
      </div>
    </div>
  );
};

export default SharedFile;
